import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@base-ui/react/dialog';
import type { InventoryLevel } from '@interfaces/ILogistics';
import type { StockFormPlumbing } from '../StockDetailDrawer';
import type { ISiteMapArea, ISiteMapPoint, SiteMapAreaColor, SiteMapAreaIcon } from '@interfaces/ISiteMap';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { Menu } from '@components/ui/Menu/Menu';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import { ZoomButtons } from '@components/ui/ZoomButtons/ZoomButtons';
import { useZoomSteps } from '@components/ui/ZoomButtons/useZoomSteps';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { CheckIcon, CollapseIcon, ExpandIcon, LayersIcon } from '@utils/icons';
import { Panel } from '../../Panel';
import { useLogisticsEditLock } from '../../revision/LogisticsEditLock';
import chrome from '../dialogChrome.module.css';
import { AreaDrawer } from './AreaDrawer';
import { MapMenu } from './MapMenu';
import { MapStage } from './MapStage';
import { NewAreaDialog } from './NewAreaDialog';
import { PlanPicker } from './PlanPicker';
import { SiteMapEditTools } from './SiteMapEditTools';
import { SiteMapOverlayHost } from './SiteMapOverlayHost';
import { useAreaDrawing } from './useAreaDrawing';
import { useEquipmentEditing } from './useEquipmentEditing';
import { useEquipmentPlacement, type SiteMapMode } from './useEquipmentPlacement';
import { useSiteMapData } from './useSiteMapData';
import { useSiteMapOverlay } from './useSiteMapOverlay';
import { pointsAttr } from './siteMap.logic';
import styles from './SiteMap.module.css';

export interface ISiteMapProps {
  siteId: string;
  levels: InventoryLevel[];
  tz: string | undefined;
  /** Surfaces a failed write as a toast — threaded from LogisticsPage's own
   *  `useToasts` `push`, matching every other panel's error-toast convention. */
  onError: (message: string) => void;
  /** Whether the card is the fixed full-viewport overlay (`SiteMapOverlayHost`
   *  portals it past the sidebar/topbar) — LogisticsPage owns the state
   *  (`mapExpanded`); this panel owns what it drives: the toolbar glyph,
   *  positioning, the aspect-bound stage, scroll lock, Esc-to-collapse. */
  expanded: boolean;
  /** Flips `expanded` — also wired to the backdrop click and Esc-collapse. */
  onToggleExpanded: () => void;
  /** The stock form's inputs, threaded to the AreaDrawer's stock companion
   *  (the same plumbing the page hands the stock dialog and drawer). */
  stockPlumbing: StockFormPlumbing;
  /** Stages a material delete from the companion — the page's DiscardModal
   *  fires it. */
  onDeleteStock: (level: InventoryLevel) => void;
  /** The caller's role may write (the page's `canEdit`). When false the draw /
   *  place tools, the ⋯ menu (change plan, clear) and the first-run "Use this
   *  plan" are not rendered, existing equipment cannot be dragged or removed,
   *  and the area drawer opens without its write controls. Panning, zoom,
   *  layers and reading stay live. */
  canEdit: boolean;
}

/**
 * Site Map panel — the editable-map container: the empty-state plan picker,
 * the area/equipment chip + polygon view, layer toggles and zoom, the area
 * drawer (rename/delete/assign stock), draw-area mode, equipment placement +
 * post-create editing, and the ⋯ menu (change base plan / clear map).
 */
export function SiteMap({
  siteId,
  levels,
  tz,
  onError,
  expanded,
  onToggleExpanded,
  stockPlumbing,
  onDeleteStock,
  canEdit,
}: ISiteMapProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  const cardRef = useRef<HTMLDivElement | null>(null);

  const { mapQuery, setPlan, createArea, updateArea, removeArea, createEquipment, updateEquipment, removeEquipment, clearMap } =
    useSiteMapData(siteId, onError);
  const { floors } = useSiteLocations(siteId);

  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [showAreas, setShowAreas] = useState(true);
  const [showRadii, setShowRadii] = useState(true);
  const zoomSteps = useZoomSteps();

  const [mode, setMode] = useState<SiteMapMode>('view');
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  // The floor the picker's user just clicked, ahead of the switch confirm
  // (spec §4.2: picker THEN confirm) — null while no switch is staged. Only
  // set for a DIFFERENT floor than the one already configured; picking the
  // current floor is a no-op close instead (see the picker's `onPick` below).
  const [stagedFloorId, setStagedFloorId] = useState<string | null>(null);

  const overlayRef = useRef<SVGSVGElement | null>(null);
  const expandToggleRef = useRef<HTMLButtonElement | null>(null);

  const data = mapQuery.data;
  const config = data?.config ?? null;

  const drawing = useAreaDrawing({ mode, setMode, overlayRef });
  const equipmentPlacement = useEquipmentPlacement({ mode, setMode, createEquipment, t });
  const equipmentEditing = useEquipmentEditing({
    equipment: data?.equipment ?? [],
    overlayRef,
    updateEquipment,
    removeEquipment,
  });

  // The area the drawer names — derived from the live list, like
  // LogisticsPage's `detailDelivery`, so a deleted area simply closes the
  // drawer instead of showing a stale record.
  const selectedArea = data?.areas.find((a) => a.areaId === selectedAreaId) ?? null;
  // The last area the drawer showed, held so Base UI's exit transition still
  // has content to slide out after a delete's refetch drops it from `data`
  // (mirrors LogisticsPage.tsx's `heldDelivery`).
  const [heldArea, setHeldArea] = useState<ISiteMapArea | null>(null);
  if (selectedArea && selectedArea !== heldArea) setHeldArea(selectedArea);

  // Body scroll lock + Esc-precedence for the fixed overlay — see
  // `useSiteMapOverlay`'s own doc comment for both effects' reasoning.
  useSiteMapOverlay({ expanded, mode, selectedArea, onToggleExpanded, toggleRef: expandToggleRef });

  const handleCreateArea = (v: { name: string; icon: SiteMapAreaIcon; color: SiteMapAreaColor }) => {
    createArea.mutate({ ...v, points: drawing.draftPoints }, { onSuccess: drawing.resetDraw });
  };

  const handleStageClick = (p: ISiteMapPoint) => {
    if (mode === 'draw') {
      drawing.handleClick(p);
    } else if (mode === 'placeEquipment' || mode === 'setRadius') {
      equipmentPlacement.handleClick(p);
    }
  };

  const handleStageMove = (p: ISiteMapPoint) => {
    if (mode === 'draw') drawing.handleMove(p);
    else if (mode === 'setRadius') equipmentPlacement.handleMove(p);
  };

  // A switch or a clear replaces the ENTIRE plan/area/equipment picture out
  // from under whatever the panel was mid-way through — a draft polygon, an
  // open drawer, an in-progress equipment placement, a live drag. All of it
  // is stale the instant the mutation lands, so every transient tool state
  // resets together rather than one at a time.
  const resetTransientState = () => {
    drawing.resetDraw();
    equipmentPlacement.reset();
    setSelectedAreaId(null);
  };

  // Every plan of the site holding a map, straight from the read — so the
  // picker tags each floor truthfully, not just the one showing.
  const plansWithMaps = data ? data.mappedFloorIds : [];

  const currentFloorName = config ? (floors.find((f) => f.id === config.floorId)?.name ?? config.floorId) : null;
  // The floor the staged switch confirm names — resolved the same way as
  // `currentFloorName` so "Switch to …?" always shows a real floor name, not
  // an internal id.
  const stagedFloorName = stagedFloorId ? (floors.find((f) => f.id === stagedFloorId)?.name ?? stagedFloorId) : '';

  const toolbarBtnSecondaryIcon = `${styles.toolbarBtn} ${styles.toolbarBtnSecondary} ${styles.toolbarBtnIcon}`;

  // Panel's title slot, composed instead of a plain string: the blueprint
  // thumbnail + the panel's own "Site Map" title, suffixed with the CURRENT
  // floor's name (em dash, e.g. "Site Map — Floor 1") once a plan is
  // configured. Composed here rather than a new i18n key with the dash baked
  // in. Before a plan is configured there's no floor name to append — the
  // bold text is just "Site Map" (title key alone), which doubles as the
  // fallback for the empty state.
  const headerTitleText = currentFloorName ? `${t('logistics.zones.title')} — ${currentFloorName}` : t('logistics.zones.title');
  const headerTitle = (
    <span className={styles.headerTitleRow}>
      <span className={styles.basePlanThumb} aria-hidden="true" />
      <span className={styles.headerPlanName}>{headerTitleText}</span>
    </span>
  );

  const toolbar = (
    <div className={styles.toolbar}>
      {canEdit && (
        <SiteMapEditTools hasPlan={config !== null} mode={mode} drawing={drawing} equipmentPlacement={equipmentPlacement} />
      )}
      <Menu.Root>
        <Menu.Trigger
          render={<button type="button" className={toolbarBtnSecondaryIcon} aria-label={t('logistics.siteMap.layers')} />}
        >
          <LayersIcon size={14} />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner side="bottom" align="end" sideOffset={4}>
            <Menu.Popup className={styles.layersPopup}>
              <Menu.CheckboxItem
                className={styles.layerItem}
                checked={showAreas}
                onCheckedChange={setShowAreas}
                closeOnClick={false}
              >
                <span className={styles.layerCheck} aria-hidden="true">
                  {showAreas ? <CheckIcon size={16} /> : null}
                </span>
                {t('logistics.siteMap.layerAreas')}
              </Menu.CheckboxItem>
              <Menu.CheckboxItem
                className={styles.layerItem}
                checked={showRadii}
                onCheckedChange={setShowRadii}
                closeOnClick={false}
              >
                <span className={styles.layerCheck} aria-hidden="true">
                  {showRadii ? <CheckIcon size={16} /> : null}
                </span>
                {t('logistics.siteMap.layerRadii')}
              </Menu.CheckboxItem>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
      <ZoomButtons
        zoom={zoomSteps}
        zoomOutLabel={t('logistics.siteMap.zoomOut')}
        zoomInLabel={t('logistics.siteMap.zoomIn')}
      />
      <button
        ref={expandToggleRef}
        type="button"
        className={toolbarBtnSecondaryIcon}
        aria-label={expanded ? t('logistics.siteMap.collapse') : t('logistics.siteMap.expand')}
        title={expanded ? t('logistics.siteMap.collapse') : t('logistics.siteMap.expand')}
        onClick={onToggleExpanded}
      >
        {expanded ? <CollapseIcon size={14} /> : <ExpandIcon size={14} />}
      </button>
      {/* Not rendered without the role. The lock shares the no-plan-yet
          fallback: the same disabled ⋯ the toolbar shows before there is a
          map to change or clear. */}
      {!canEdit ? null : config && !locked ? (
        <MapMenu
          triggerClassName={toolbarBtnSecondaryIcon}
          onChangePlan={() => setShowPlanPicker(true)}
          onClearMap={() => {
            clearMap.mutate();
            resetTransientState();
          }}
        />
      ) : (
        <KebabButton className={toolbarBtnSecondaryIcon} aria-label={t('logistics.siteMap.more')} disabled />
      )}
    </div>
  );

  const renderBody = () => {
    // The shared component's default copy blames Genba — wrong here: the
    // site map is D-ORCA-owned and never touches that provider.
    if (mapQuery.isError) return <DataUnavailable message={t('logistics.siteMap.loadError')} />;
    if (mapQuery.isLoading || !data) return <div className={styles.shimmer} role="status" aria-live="polite" />;
    if (!config) {
      // First-run: nothing to lose yet, so "Use this plan" commits directly.
      // `expanded` lets the picker flex-fill the fullscreen overlay the same
      // way MapStage's own `.stageOuter[data-expanded]` does.
      return (
        <PlanPicker
          siteId={siteId}
          plansWithMaps={plansWithMaps}
          onPick={(floorId) => setPlan.mutate(floorId)}
          busy={setPlan.isPending}
          expanded={expanded}
          canEdit={canEdit}
        />
      );
    }
    // Vertex handles need a fixed on-screen size regardless of the
    // blueprint's plan-unit scale (a real floor plan's viewBox can be
    // thousands of units wide — see `.radiusCircle`'s comment below). A
    // `<rect>`'s `width`/`height` are fill geometry, not stroke, so
    // `non-scaling-stroke` alone can't fix it the way it fixes stroke widths
    // — `drawing.unitsPerPx` state (kept fresh by the draw handlers in
    // `useAreaDrawing`) stands in for a render-time ref read.
    const vertexSide = drawing.unitsPerPx * 16;
    // The lasso reference draws ONE dashed path through every placed point
    // AND the live cursor — not a separate "placed" polyline plus a distinct
    // cursor segment — so the in-progress edge reads identically to the
    // already-placed ones.
    const pathPoints = drawing.cursor ? [...drawing.draftPoints, drawing.cursor] : drawing.draftPoints;
    const draftLayer =
      mode === 'draw' ? (
        <>
          {pathPoints.length > 1 ? <polyline points={pointsAttr(pathPoints)} className={styles.draftPath} /> : null}
          {drawing.draftPoints.map((p, i) => {
            const isFirst = i === 0;
            // Once ≥3 points are down, the first vertex IS the close target
            // (a click within the screen threshold of it, or a direct click
            // on the handle itself, closes the polygon, same as Enter). It
            // renders at the SAME size as every other handle by default —
            // the CSS-only hover-grow on `.draftVertexClosable`
            // (SiteMap.module.css) is the affordance, not a permanent size
            // difference.
            const isCloseTarget = isFirst && drawing.canClose;
            const side = vertexSide;
            return (
              <rect
                key={i}
                x={p.x - side / 2}
                y={p.y - side / 2}
                width={side}
                height={side}
                className={isCloseTarget ? `${styles.draftVertex} ${styles.draftVertexClosable}` : styles.draftVertex}
                data-testid="draft-vertex"
                data-close-target={isCloseTarget ? 'true' : undefined}
                onClick={
                  isCloseTarget
                    ? (e) => {
                        // The draft layer sits above the interactive overlay
                        // `<svg>` that owns stage clicks (`.draftLayer` is
                        // `pointer-events: none` except this rect — see the
                        // CSS). Stop it here so a direct hit on the handle
                        // closes the polygon once, not that PLUS a stage
                        // click adding a 4th vertex underneath.
                        e.stopPropagation();
                        drawing.closePolygon();
                      }
                    : undefined
                }
              />
            );
          })}
        </>
      ) : null;
    const drawHints =
      mode === 'draw' ? (
        <div className={styles.hintRow}>
          <span className={styles.hintChip}>{t('logistics.siteMap.hint.addPoint')}</span>
          <span className={styles.hintChip}>{t('logistics.siteMap.hint.close')}</span>
          <span className={styles.hintChip}>{t('logistics.siteMap.hint.cancel')}</span>
        </div>
      ) : null;
    const draft = mode === 'draw' ? draftLayer : mode === 'setRadius' ? equipmentPlacement.draft : null;
    const hint =
      mode === 'draw' ? (
        drawHints
      ) : equipmentPlacement.hint ? (
        <div className={styles.hintRow}>{equipmentPlacement.hint}</div>
      ) : null;
    // Existing equipment can only be dragged/removed in plain view mode — a
    // draw or place tool already owns the stage's clicks/moves.
    const editingEnabled = mode === 'view' && canEdit && !locked;
    return (
      <>
        <MapStage
          siteId={siteId}
          floorId={config.floorId}
          areas={data.areas}
          equipment={equipmentEditing.displayEquipment}
          levels={levels}
          selectedAreaId={selectedAreaId}
          onAreaClick={setSelectedAreaId}
          showAreas={showAreas}
          showRadii={showRadii}
          zoom={zoomSteps.zoom}
          draft={draft}
          hint={hint}
          onStageClick={mode === 'draw' || mode === 'placeEquipment' || mode === 'setRadius' ? handleStageClick : undefined}
          onStageMove={mode === 'draw' || mode === 'setRadius' ? handleStageMove : undefined}
          overlayRef={overlayRef}
          equipmentDraft={equipmentPlacement.chipDraft}
          onEquipmentRadiusPointerDown={editingEnabled ? equipmentEditing.onRadiusPointerDown : undefined}
          onEquipmentChipPointerDown={editingEnabled ? equipmentEditing.onChipPointerDown : undefined}
          onEquipmentRemove={editingEnabled ? equipmentEditing.requestRemove : undefined}
          expanded={expanded}
        />
        <ul className={styles.legend}>
          <li className={styles.legendItem}>
            <span className={styles.legendSwatch} aria-hidden="true" />
            {t('logistics.siteMap.legendArea')}
          </li>
          <li className={styles.legendItem}>
            <span className={styles.legendRing} aria-hidden="true" />
            {t('logistics.siteMap.legendRadius')}
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      {/* Owns WHERE the card renders (see its own doc comment). */}
      <SiteMapOverlayHost expanded={expanded} onToggleExpanded={onToggleExpanded}>
        <div ref={cardRef} data-testid="site-map-card" data-expanded={expanded ? 'true' : undefined} className={styles.card}>
          <Panel title={headerTitle} subtitle={t('logistics.zones.subtitle')} headerRight={toolbar} fill>
            {renderBody()}
          </Panel>
        </div>
      </SiteMapOverlayHost>
      <AreaDrawer
        area={selectedArea ?? heldArea}
        open={selectedArea !== null}
        areas={data?.areas ?? []}
        levels={levels}
        tz={tz}
        stockPlumbing={stockPlumbing}
        onDeleteStock={onDeleteStock}
        onClose={() => setSelectedAreaId(null)}
        onRename={(name) => {
          if (selectedArea) updateArea.mutate({ areaId: selectedArea.areaId, patch: { name } });
        }}
        onAssignMaterials={(materials) => {
          if (selectedArea) updateArea.mutate({ areaId: selectedArea.areaId, patch: { materials } });
        }}
        onDelete={() => {
          if (selectedArea) removeArea.mutate(selectedArea.areaId);
          setSelectedAreaId(null);
        }}
        canEdit={canEdit}
      />
      {/* `pending` disables Create while the POST is in flight: a second click
          would create the same area twice (the revision gate adopts the first
          response, so it does not catch it). */}
      <NewAreaDialog
        open={drawing.newAreaOpen}
        pending={createArea.isPending}
        onCancel={drawing.resetDraw}
        onCreate={handleCreateArea}
      />

      {/* The ⋯ menu's "Change base plan…" hands off to `onChangePlan` DIRECTLY
          (no confirm inside MapMenu) — spec §4.2's menu flow is pick-then-
          confirm: this picker opens first, and picking a floor OTHER than the
          one already configured stages it for the switch confirm below,
          which names the concrete floor being switched to. Picking the
          current floor is a no-op close (nothing to confirm). */}
      <Dialog.Root
        open={showPlanPicker}
        onOpenChange={(next) => {
          if (!next) {
            setShowPlanPicker(false);
            setStagedFloorId(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Backdrop className={chrome.backdrop} />
          <Dialog.Popup className={`${chrome.popup} ${styles.planPickerPopup}`}>
            {/* PlanPicker renders its own visible "Choose the base plan" rail
                title — this one exists only to give the dialog an accessible
                name (Base UI's `aria-labelledby`), not to show twice. */}
            <Dialog.Title className="visually-hidden">{t('logistics.siteMap.emptyTitle')}</Dialog.Title>
            <ScrollArea>
              <PlanPicker
                siteId={siteId}
                plansWithMaps={plansWithMaps}
                onPick={(floorId) => {
                  if (floorId === config?.floorId) {
                    setShowPlanPicker(false);
                    return;
                  }
                  setStagedFloorId(floorId);
                }}
                canEdit={canEdit}
              />
            </ScrollArea>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>

      <DiscardModal
        open={stagedFloorId !== null}
        title={t('logistics.siteMap.switchTitle', { floor: stagedFloorName })}
        description={t('logistics.siteMap.switchBody')}
        confirmLabel={t('logistics.siteMap.switchConfirm')}
        cancelLabel={t('logistics.siteMap.cancel')}
        onCancel={() => setStagedFloorId(null)}
        onConfirm={() => {
          if (stagedFloorId) {
            setPlan.mutate(stagedFloorId);
            resetTransientState();
          }
          setStagedFloorId(null);
          setShowPlanPicker(false);
        }}
      />

      <DiscardModal
        open={equipmentEditing.removingId !== null}
        title={t('logistics.siteMap.equipment.remove')}
        onCancel={equipmentEditing.cancelRemove}
        onConfirm={equipmentEditing.confirmRemove}
      />
    </>
  );
}
